import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  Cell 
} from "recharts";
import { getAppIcon, getAppColor, calculateUsagePercentage, cn, isLicenseExpired } from "@/lib/utils";
import { Application, License } from "@shared/schema";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { TrendingDown, TrendingUp, AlertCircle, CheckCircle } from "lucide-react";

export default function ApplicationUsageChart() {
  const [sortBy, setSortBy] = useState("usage");
  
  const { data: applications, isLoading: appsLoading } = useQuery({
    queryKey: ['/api/applications'],
  });
  
  const { data: licenses, isLoading: licensesLoading } = useQuery({
    queryKey: ['/api/licenses'],
  });
  
  const isLoading = appsLoading || licensesLoading;

  // Transform data for the chart
  const getChartData = () => {
    if (!applications || !licenses || !Array.isArray(applications) || !Array.isArray(licenses)) return [];
    
    const appUsageData = applications.map((app: Application) => {
      // Find active (non-expired, non-discontinued) licenses for this app
      const appLicenses = licenses.filter((license: License) => {
        if (license.applicationId !== app.id) return false;
        if (license.status === 'discontinued') return false;
        const isLifetime = license.billingCycle === 'Lifetime' || license.billingCycle === 'One-Time';
        if (!isLifetime && isLicenseExpired(license.renewalDate)) return false;
        return true;
      });

      // Calculate total seats and assigned seats
      const totalSeats = appLicenses.reduce((sum: number, license: License) => sum + license.totalSeats, 0);
      const assignedSeats = appLicenses.reduce((sum: number, license: License) => sum + license.assignedSeats, 0);
      
      // Calculate usage percentage
      const usagePercentage = calculateUsagePercentage(assignedSeats, totalSeats);
      
      return {
        id: app.id,
        name: app.name,
        logo: app.logo,
        category: app.category,
        totalSeats,
        assignedSeats,
        usagePercentage,
        inactiveSeats: totalSeats - assignedSeats
      };
    });
    
    // Sort data based on selected sort option
    return appUsageData.sort((a: any, b: any) => {
      if (sortBy === "usage") {
        return b.usagePercentage - a.usagePercentage;
      } else if (sortBy === "seats") {
        return b.totalSeats - a.totalSeats;
      } else {
        return b.assignedSeats - a.assignedSeats;
      }
    });
  };

  const allAppsData = getChartData();
  const chartData = allAppsData.slice(0, 10); // Top 10 for display only

  // Calculate summary statistics from ALL apps, not just top 10
  const totalSeatsAcrossAll = allAppsData.reduce((sum: number, app: any) => sum + app.totalSeats, 0);
  const totalAssignedAcrossAll = allAppsData.reduce((sum: number, app: any) => sum + app.assignedSeats, 0);
  const overallUtilization = totalSeatsAcrossAll > 0 ? Math.round((totalAssignedAcrossAll / totalSeatsAcrossAll) * 100) : 0;
  const underutilizedApps = allAppsData.filter((app: any) => app.totalSeats > 0 && app.usagePercentage < 70).length;
  const wastedSeats = allAppsData.reduce((sum: number, app: any) => sum + app.inactiveSeats, 0);

  const getUtilizationColor = (percentage: number) => {
    if (percentage >= 80) return '#10b981'; // green
    if (percentage >= 50) return '#f59e0b'; // amber
    return '#ef4444'; // red
  };

  const customTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border border-slate-300 shadow-lg rounded-lg min-w-[240px]">
          <p className="font-semibold text-slate-900 mb-3">{data.name}</p>
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Assigned:</span>
              <span className="text-sm font-semibold text-emerald-600">{data.assignedSeats} seats</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-slate-600">Available:</span>
              <span className="text-sm font-semibold text-slate-500">{data.inactiveSeats} seats</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-slate-200">
              <span className="text-sm font-medium text-slate-700">Total Seats:</span>
              <span className="text-sm font-bold text-slate-900">{data.totalSeats}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-slate-700">Utilization:</span>
              <span className={cn(
                "text-sm font-bold",
                data.usagePercentage >= 80 ? "text-emerald-600" : 
                data.usagePercentage >= 50 ? "text-amber-600" : "text-red-600"
              )}>{data.usagePercentage}%</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {!isLoading && chartData.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Seats</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{totalSeatsAcrossAll.toLocaleString()}</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Overall Utilization</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{overallUtilization}%</p>
                </div>
                <div className={cn(
                  "h-12 w-12 rounded-lg flex items-center justify-center",
                  overallUtilization >= 80 ? "bg-emerald-100" : overallUtilization >= 50 ? "bg-amber-100" : "bg-red-100"
                )}>
                  {overallUtilization >= 70 ? (
                    <CheckCircle className={cn(
                      "h-6 w-6",
                      overallUtilization >= 80 ? "text-emerald-600" : "text-amber-600"
                    )} />
                  ) : (
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Underutilized Apps</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{underutilizedApps}</p>
                  <p className="text-xs text-slate-500 mt-1">Below 70% usage</p>
                </div>
                <div className="h-12 w-12 bg-amber-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Unused Seats</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{wastedSeats.toLocaleString()}</p>
                  <p className="text-xs text-slate-500 mt-1">Potential savings</p>
                </div>
                <div className="h-12 w-12 bg-red-100 rounded-lg flex items-center justify-center">
                  <TrendingDown className="h-6 w-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Chart */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <div className="flex items-center space-x-2">
            <CardTitle className="text-lg font-semibold">Application Utilization Details</CardTitle>
            <Popover>
              <PopoverTrigger>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <InfoCircledIcon className="h-4 w-4 text-slate-500" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80">
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-slate-900">How to Read This Chart</h4>
                  <p className="text-sm text-slate-600">
                    Each bar shows seat allocation per application. Green indicates assigned seats, gray shows unused capacity.
                  </p>
                  <div className="space-y-2 pt-2 border-t border-slate-200">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-emerald-500"></div>
                      <span className="text-xs text-slate-700">≥80% = Excellent utilization</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-amber-500"></div>
                      <span className="text-xs text-slate-700">50-79% = Room for optimization</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-red-500"></div>
                      <span className="text-xs text-slate-700">&lt;50% = Significant waste</span>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[190px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="usage">Sort by Utilization %</SelectItem>
              <SelectItem value="seats">Sort by Total Seats</SelectItem>
              <SelectItem value="active">Sort by Assigned Seats</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-96 w-full" />
            </div>
          ) : chartData.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-slate-500">No application data available</p>
            </div>
          ) : (
            <div className="space-y-4">
              {chartData.map((app: any, index: number) => (
                <div key={app.id} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`h-8 w-8 rounded-lg ${getAppColor(app?.logo || '')} flex items-center justify-center flex-shrink-0`}>
                        <i className={`${getAppIcon(app?.logo || '')} text-sm`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-slate-900 truncate">{app.name}</p>
                        <p className="text-xs text-slate-500">{app.assignedSeats} of {app.totalSeats} seats used</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge 
                        variant={app.usagePercentage >= 80 ? "default" : app.usagePercentage >= 50 ? "secondary" : "destructive"}
                        className={cn(
                          "font-semibold",
                          app.usagePercentage >= 80 ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200" :
                          app.usagePercentage >= 50 ? "bg-amber-100 text-amber-700 hover:bg-amber-200" :
                          "bg-red-100 text-red-700 hover:bg-red-200"
                        )}
                      >
                        {app.usagePercentage}%
                      </Badge>
                    </div>
                  </div>
                  <div className="relative">
                    <Progress 
                      value={app.usagePercentage} 
                      className="h-3"
                      style={{
                        ['--progress-background' as any]: getUtilizationColor(app.usagePercentage)
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
